import find from 'lodash/find';
import moment from 'moment';
import { MAIN_DATA } from '@mos-front-components/public/es/components/MainData';
import {
  PERMITTED_DOCUMENTS_PASSPORT_RF,
  PERMITTED_DOCUMENTS_MILITARY_ID,
  PERMITTED_DOCUMENTS_NOT_PASSPORT,
  PERMITTED_DOCUMENTS_FOR_CHILD
} from '../store/passport/permittedDocuments';
import { GIA, NOT_CORRECT_EXAM_FROM } from '../constants/validationFromName';
import { MAIN_DATA_CHILD } from '../constants/applicant';
import { BIRTH_REG_PLACES } from '../constants/citizenship';
import {
  ACT_RECORD_FIELD_NAMES,
  ACT_RECORD_NUMBER_OLD_REGEXP,
  ACT_RECORD_NUMBER_REGEXP,
  CHANGE_ACT_RECORD_FIELD_NAMES
} from '../constants/actRecord';
import { DATE_FORMAT } from '../helpers/date';
import { VALIDATION_MESSAGES } from './constants';

const emailValidate = {
  required: true,
  maxLength: 100,
  pattern: /^([a-zA-Z0-9]+[a-zA-Z0-9-._]*[a-zA-Z0-9]*@[a-zA-Z0-9]+[a-zA-Z0-9-._]*[a-zA-Z0-9]\.[a-zA-Z]{2}[a-zA-Z]?)$/i,
  ...VALIDATION_MESSAGES.Email
};

const phoneValidate = {
  required: true,
  pattern: '^([+]7)?[0-9]{10}$',
  ...VALIDATION_MESSAGES.Phone
};

export const snilsValidate = {
  required: true,
  pattern: /(^[0-9]{3}-[0-9]{3}-[0-9]{3} [0-9]{2}$)|(^[0-9]{11}$)/i,
  ...VALIDATION_MESSAGES.Snils
};

export const birthRegistrationPlaceValidator = {
  field: BIRTH_REG_PLACES.NAME,
  required: false, // Проверка пока ручная в validation.js
  messageRequired: BIRTH_REG_PLACES.PLACEHOLDER
};

export const actRecordNumberValidator = forChangePersonalData => ({
  field: (forChangePersonalData
    ? CHANGE_ACT_RECORD_FIELD_NAMES
    : ACT_RECORD_FIELD_NAMES
  ).number,
  required: true,
  pattern: ACT_RECORD_NUMBER_REGEXP.FROM_VALIDATOR,
  ...VALIDATION_MESSAGES.ActRecordNumber
});

export const actRecordNumberOldValidator = forChangePersonalData => ({
  field: (forChangePersonalData
    ? CHANGE_ACT_RECORD_FIELD_NAMES
    : ACT_RECORD_FIELD_NAMES
  ).number,
  required: true,
  pattern: ACT_RECORD_NUMBER_OLD_REGEXP.FROM_VALIDATOR,
  ...VALIDATION_MESSAGES.ActRecordNumber
});

const MIN_DATE = '01.01.1900';

export const actRecordDateValidator = forChangePersonalData => ({
  field: (forChangePersonalData
    ? CHANGE_ACT_RECORD_FIELD_NAMES
    : ACT_RECORD_FIELD_NAMES
  ).date,
  date: true,
  required: true,
  dateRange: true,
  minDate: moment(MIN_DATE, DATE_FORMAT),
  maxDate: moment(),
  ...VALIDATION_MESSAGES.ActRecordDate
});

export const actRecordRegPlaceValidator = forChangePersonalData => ({
  field: (forChangePersonalData
    ? CHANGE_ACT_RECORD_FIELD_NAMES
    : ACT_RECORD_FIELD_NAMES
  ).regPlace,
  required: true,
  ...VALIDATION_MESSAGES.ActRecordRegPlace
});

const actRecordValidators = [
  actRecordNumberValidator(),
  actRecordDateValidator(),
  actRecordRegPlaceValidator()
];

const actRecordOldValidators = [
  actRecordNumberOldValidator(),
  actRecordDateValidator(),
  actRecordRegPlaceValidator()
];

const getDocsParameters = (withBirthRegPlace, params) => {
  const documentValidatorsBase = [];
  const dateNow = moment();
  if (withBirthRegPlace) {
    documentValidatorsBase.push(birthRegistrationPlaceValidator);
  }
  return [
    ...documentValidatorsBase,
    {
      field: 'number',
      required: true,
      ...params,
      ...VALIDATION_MESSAGES.DocumentNumber
    },
    {
      field: 'byWhom',
      required: true,
      maxLength: 250,
      ...VALIDATION_MESSAGES.DocumentIssuer
    },
    {
      field: 'dateOfIssue',
      date: true,
      required: true,
      dateRange: true,
      minDate: moment(MIN_DATE, DATE_FORMAT),
      maxDate: dateNow,
      ...VALIDATION_MESSAGES.DocumentDate
    }
  ];
};

const getValidationRules = (
  documentType,
  actRecordVersion,
  withBirthRegPlace
) => {
  const documentValidatorsBase = [];
  const dateNow = moment();

  if (withBirthRegPlace) {
    documentValidatorsBase.push(birthRegistrationPlaceValidator);
  }

  switch (documentType) {
    case find(
      PERMITTED_DOCUMENTS_PASSPORT_RF,
      document => document === documentType,
      'DOCUMENT_NOT_FOUND'
    ):
      const rf_params = { length: 10 };
      return getDocsParameters(withBirthRegPlace, rf_params);
    case find(
      PERMITTED_DOCUMENTS_FOR_CHILD,
      document => document === documentType,
      'DOCUMENT_NOT_FOUND'
    ):
      const child_params = { minLength: 0, maxLength: 50 };
      return getDocsParameters(withBirthRegPlace, child_params);
    case find(
      PERMITTED_DOCUMENTS_MILITARY_ID,
      document => document === documentType,
      'DOCUMENT_NOT_FOUND'
    ):
      const military_params = { length: 9 };
      return getDocsParameters(withBirthRegPlace, military_params);
    case find(
      PERMITTED_DOCUMENTS_NOT_PASSPORT,
      document => document === documentType,
      'DOCUMENT_NOT_FOUND'
    ):
      const not_passport_params = {
        maxLength: 50,
        messageRequired: 'Укажите серию и номер документа',
        messageLength: 'Разрешено ввести не более 50 знаков'
      };

      return getDocsParameters(withBirthRegPlace, not_passport_params);

    case MAIN_DATA:
      return [
        ...documentValidatorsBase,
        {
          field: 'phone',
          ...phoneValidate
        },
        {
          field: 'firstName',
          required: true,
          messageRequired: 'Укажите имя'
        },
        {
          field: 'lastName',
          required: true,
          messageRequired: 'Укажите фамилию'
        },
        {
          field: 'birthdate',
          required: true,
          date: true,
          dateRange: true,
          minDate: moment(MIN_DATE, DATE_FORMAT),
          maxDate: dateNow,
          ...VALIDATION_MESSAGES.Birthdate
        },
        {
          field: 'email',
          ...emailValidate
        },
        {
          field: 'gender',
          required: true,
          messageRequired: 'Укажите пол'
        },
        {
          field: 'citizenship',
          required: false,
          messageRequired: 'Укажите гражданство'
        }
      ];

    case MAIN_DATA_CHILD:
      return [
        ...documentValidatorsBase,
        {
          field: 'phone',
          ...phoneValidate
        },
        {
          field: 'firstName',
          required: true,
          messageRequired: 'Укажите имя'
        },
        {
          field: 'lastName',
          required: true,
          messageRequired: 'Укажите фамилию'
        },
        {
          field: 'birthdate',
          required: true,
          date: true,
          dateRange: true,
          minDate: moment(MIN_DATE, DATE_FORMAT),
          maxDate: dateNow,
          ...VALIDATION_MESSAGES.Birthdate
        },
        {
          field: 'email',
          ...emailValidate
        },
        {
          field: 'gender',
          required: true,
          messageRequired: 'Укажите пол'
        },
        {
          field: 'snils',
          ...snilsValidate
        },
        {
          field: 'citizenship',
          required: false,
          messageRequired: 'Укажите гражданство'
        }
      ];

    case GIA:
      return [
        {
          field: 'document_number',
          required: true,
          maxLength: 16,
          messageRequired: 'Укажите номер',
          messageLength: 'Разрешено ввести не более 16 знаков'
        },
        {
          field: 'registration_code',
          required: true,
          minLength: 12,
          maxLength: 12,
          messageRequired: 'Укажите код',
          messageLength: 'Разрешено ввести ровно 12 знаков'
        }
      ];

    case NOT_CORRECT_EXAM_FROM:
      return [
        {
          field: 'phone',
          ...phoneValidate
        },
        {
          field: 'email',
          ...emailValidate
        },
        {
          field: 'comment',
          required: true,
          messageRequired: 'Введите ваш комментарий'
        }
      ];

    case 'act_record':
      if (withBirthRegPlace) {
        return actRecordVersion === 'OLD'
          ? actRecordOldValidators
          : actRecordValidators;
      }

      return [];

    default:
      return [];
  }
};

export default getValidationRules;
